import { isAfter, subMonths } from 'date-fns';
import { getSession } from 'next-auth/react';
import { PupInfoForm, schema as pupInfoSchema } from '../PupInfoForm';
import { ActivityForm, schema as activitySchema } from '../ActivityForm';
import { CaloriesForm, schema as caloriesSchema } from '../CaloriesForm';
import { UserInfoForm, schema as userInfoSchema } from '../UserInfoForm';
import { BreedForm, schema as breedSchema } from '../BreedForm';
import { AllergiesForm, schema as allergiesSchema } from '../AllergiesForm';
import { BirthdayForm, schema as birthdaySchema } from '../BirthdayForm';
import {
  USER_INFO_SLUG,
  PUP_INFO_SLUG,
  BIRTHDAY_SLUG,
  BREED_SLUG,
  CALORIES_SLUG,
  ACTIVITY_SLUG,
  ALLERGIES_SLUG,
} from '../routing';
import { ApiResult, register, wrapErrorWithResult } from '../../api';

/**
 * TODO(James) If this is more useful as something more generic, extract or wrap in some way.
 * The `result` generation logic is specific to this form.
 *
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 * @typedef {AxiosResponse | ReturnType<typeof register>} SaveFormReturn
 * @param {(data: Object, apis: Apis) => Promise<SaveFormReturn>} saveForm
 * @returns {(data: Object, apis: Apis) => Promise<ApiResult<{}>>}
 */
const wrapSaveFormWithResult = saveForm => async (data, apis) => {
  try {
    const response = await saveForm(data, apis);

    let payload;

    if (response.ok === true) {
      // Sign in result.
      const session = await getSession();
      const response = await apis.customerApi.getCustomer(session.user.id);
      payload = {
        model: 'customer',
        action: session.user.is_new ? 'create' : 'update',
        customer: response.data,
      };
    } else {
      // Axios response.
      payload = {
        model: response.config.url.includes('pets') ? 'pet' : 'customer',
        action: response.status === 201 ? 'create' : 'update',
      };
      payload[payload.model] = response.data;
    }

    return ApiResult.success(payload);
  } catch (err) {
    return wrapErrorWithResult(err);
  }
};

/**
 * Get the steps needed for the quiz flow.
 *
 * @returns {Step[]}
 *
 * @typedef {Object} Step
 * @property {string} slug
 * @property {Object} component
 * @property {Object} schema
 * @property {(currentData: CurrentData) => Object} getDefaultValues
 * @property {(currentData: CurrentData) => Object} [getExtraProps]
 * @property {(formData: Object, apis: Apis) => Promise<ApiResult<any>>} saveForm
 *
 * @typedef {Object} Apis
 * @property {ReturnType<typeof import('../../customer').useSessionCustomer>} customerApi
 * @property {ReturnType<typeof import('../../pets').usePet>} petApi
 *
 * @typedef {Object} CurrentData
 * @property {Object} customer
 * @property {Object} pet
 */
export const getSteps = () => [
  {
    slug: USER_INFO_SLUG,
    component: UserInfoForm,
    schema: userInfoSchema,
    getDefaultValues: ({ customer }) => ({
      firstName: customer.first_name,
      email: customer.email,
    }),
    saveForm: wrapSaveFormWithResult(
      async ({ firstName, email }, { customerApi }) => {
        if (customerApi.customer.id) {
          try {
            return await customerApi.updateCustomer({
              first_name: firstName,
              email,
            });
          } catch (err) {
            const result = wrapErrorWithResult(err);
            // If we get an email conflict, we're going to fall
            // threw to register & try to take over the account.
            // If that account is a subscriber, the downstream
            // codepath will handle that appropriately.
            if (result.error.type !== 'emailConflict') {
              throw err;
            }
          }
        }

        const results = await register({ firstName, email });

        if (!results.ok || results.error) {
          throw results;
        }

        return results;
      },
    ),
  },
  {
    slug: PUP_INFO_SLUG,
    component: PupInfoForm,
    schema: pupInfoSchema,
    getDefaultValues: ({ pet }) => ({
      gender: pet.gender || '',
      name: pet.name || '',
    }),
    saveForm: wrapSaveFormWithResult(({ gender, name }, { petApi }) =>
      petApi.savePet({
        gender,
        name,
      }),
    ),
  },
  {
    slug: BIRTHDAY_SLUG,
    component: BirthdayForm,
    schema: birthdaySchema,
    getDefaultValues: ({ pet }) => {
      const [year, month, day] = pet.birth_date?.split('-') ?? ['', '', ''];
      return {
        year: year,
        month: month,
        day: day,
      };
    },
    saveForm: wrapSaveFormWithResult(({ year, month, day }, { petApi }) => {
      const birthDate = `${year}-${month}-${+day || 1}`;
      return petApi.savePet({
        birth_date: birthDate,
      });
    }),
  },
  {
    slug: BREED_SLUG,
    component: BreedForm,
    schema: breedSchema,
    getDefaultValues: ({ pet }) => ({
      breeds:
        pet.breeds?.map(breed => ({
          value: breed.id,
          label: breed.name,
        })) ?? [],
    }),
    saveForm: wrapSaveFormWithResult(({ breeds }, { petApi }) => {
      const newBreeds = breeds.map(breed => breed.value);
      return petApi.savePet({
        breeds: newBreeds,
      });
    }),
  },
  {
    slug: CALORIES_SLUG,
    component: CaloriesForm,
    schema: caloriesSchema,
    getDefaultValues: ({ pet }) => ({
      weight: pet.weight,
      idealWeight: pet.ideal_weight,
      spayedStatus: pet.is_neutered,
    }),
    getExtraProps: ({ pet }) => {
      return {
        isPuppy: isAfter(new Date(pet.birth_date), subMonths(Date.now(), 12)),
      };
    },
    saveForm: wrapSaveFormWithResult(
      ({ weight, idealWeight, spayedStatus }, { petApi }) =>
        petApi.savePet({
          ideal_weight: idealWeight,
          weight,
          is_neutered: spayedStatus,
        }),
    ),
  },
  {
    slug: ACTIVITY_SLUG,
    component: ActivityForm,
    schema: activitySchema,
    getDefaultValues: ({ pet }) => ({
      activity: pet.activity_level,
    }),
    saveForm: wrapSaveFormWithResult(({ activity }, { petApi }) =>
      petApi.savePet({ activity_level: activity }),
    ),
  },
  {
    slug: ALLERGIES_SLUG,
    component: AllergiesForm,
    schema: allergiesSchema,
    getDefaultValues: ({ pet }) => ({
      allergies:
        pet.allergies?.map(({ id, name }) => ({
          label: name,
          value: id,
        })) ?? [],
    }),
    saveForm: wrapSaveFormWithResult(async ({ allergies }, { petApi }) => {
      const allergyIds = allergies.map(({ value }) => value);
      const result = await petApi.savePet({
        allergies: allergyIds,
        completed_onboarding_form: true,
      });
      return result;
    }),
  },
];
